import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useKpi } from '../../context/KpiContext';
import { isDevOnlyFeatureEnabled } from '../../utils/env';

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast, showConfirm } = useToast();
  const { kpis, setKpis } = useKpi();
  const [employee, setEmployee] = useState(null);
  const [programOptions, setProgramOptions] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  const [divisionOptions, setDivisionOptions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [assignDivisionLoading, setAssignDivisionLoading] = useState(false);
  // Tab "Performance & Attendance" masih POC/dummy data -- default ke 'roles'
  // di staging/production (lihat isDevOnlyFeatureEnabled).
  const [activeTab, setActiveTab] = useState(isDevOnlyFeatureEnabled() ? 'insights' : 'roles');

  // ========================================================
  // POC DUMMY STATE - PERFORMANCE MONITORING & METRICS
  // ========================================================
  const [weeklyPlans, setWeeklyPlans] = useState([
    {
      id: 101,
      period: '4-8 May 2026',
      status: 'SUBMITTED',
      tasks: [
        { id: 1, title: 'Developed version 4 of the program timeline', date: 'Monday, 4 May 2026', evidenceUrl: 'https://docs.google.com/spreadsheets/d/example1', weight: 4, completion: 100, status: 'DONE', kpiId: 'kpi-1' },
        { id: 2, title: 'Conducted a simulation for VECI data input', date: 'Monday, 4 May 2026', evidenceUrl: 'https://docs.google.com/presentation/d/example2', weight: 3, completion: 100, status: 'DONE', kpiId: 'kpi-2' },
        { id: 3, title: 'Designed a targeted registration strategy to engage local youth participants', date: '5-6 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example3', weight: 5, completion: 100, status: 'DONE', kpiId: 'kpi-1' },
        { id: 4, title: 'Developed output templates for the Social Mapping Report', date: '6-7 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example4', weight: 4, completion: 100, status: 'DONE', kpiId: 'kpi-1' },
        { id: 5, title: 'Revised and refined key input points for the Social Mapping Guideline Book', date: 'Thursday, 7 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example5', weight: 3, completion: 100, status: 'DONE', kpiId: 'kpi-1' },
        { id: 6, title: 'Created performance and program evaluation instruments on the CLP website', date: 'Thursday, 7 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example6', weight: 5, completion: 100, status: 'DONE', kpiId: 'kpi-1' },
        { id: 7, title: 'Broke down and defined the roles of the BCF team in executing the registration strategy', date: 'Thursday, 7 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example7', weight: 3, completion: 100, status: 'DONE' },
        { id: 8, title: 'Developed a recruitment strategy for Field Supervising Lecturers', date: 'Friday, 8 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example8', weight: 4, completion: 100, status: 'DONE' },
        { id: 9, title: 'Designed a three-layer interview process for candidate selection', date: 'Friday, 8 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example9', weight: 4, completion: 100, status: 'DONE' },
        { id: 10, title: 'Prepared the minutes of Mr. Jimmy’s dissertation defense Q&A session', date: 'Tuesday, 5 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example10', weight: 2, completion: 100, status: 'DONE' },
        { id: 11, title: 'Participated in a program-specific discussion with Mr. Jimmy', date: 'Thursday, 7 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example11', weight: 2, completion: 100, status: 'DONE' },
        { id: 12, title: 'Joined a PRD one-pager discussion with the KM and IT', date: '8 May 2026', evidenceUrl: 'https://docs.google.com/document/d/example12', weight: 2, completion: 80, status: 'IN_PROGRESS' }
      ]
    },
    {
      id: 102,
      period: '11-13 May 2026',
      status: 'PLAN',
      tasks: [
        { id: 13, title: 'Discuss Campus Leaders Program implementation 2026 with Head Program', date: 'TBA', evidenceUrl: '', weight: 3, completion: 0, status: 'TODO' },
        { id: 14, title: 'Conduct discussions regarding DPL account ownership, as well as the DPL satisfaction and impact evaluation system', date: 'Monday, 11 May 2026', evidenceUrl: '', weight: 4, completion: 0, status: 'TODO' },
        { id: 15, title: 'Adjust the registration timeline', date: 'Monday, 11 May 2026', evidenceUrl: '', weight: 2, completion: 0, status: 'TODO' },
        { id: 16, title: 'Revise the monthly capacity building materials and timeline', date: 'Monday, 11 May 2026', evidenceUrl: '', weight: 3, completion: 0, status: 'TODO' },
        { id: 17, title: 'Develop the monthly activity plan and expected outputs for Project Officers', date: '11-13 May 2026', evidenceUrl: '', weight: 5, completion: 0, status: 'TODO' },
        { id: 18, title: 'Continue developing the CLP introduction deck', date: '11-13 May 2026', evidenceUrl: '', weight: 4, completion: 0, status: 'TODO' },
        { id: 19, title: 'Join a knowledge-sharing session on the PFI consultant workflow with Mr. Setyo', date: 'Wednesday, 13 May 2026', evidenceUrl: '', weight: 3, completion: 0, status: 'TODO' }
      ]
    }
  ]);

  // ========================================================
  // KPI MANAGEMENT - Manager Only
  // KPI milik karyawan secara keseluruhan (bukan per-periode), supaya tidak
  // perlu didefinisikan ulang di setiap periode/weeklyPlan. Task di periode
  // manapun bisa terhubung ke KPI yang sama lewat kpiId, dan actual dihitung
  // otomatis dari progress semua task yang terhubung, lintas periode.
  // Data KPI sendiri (title/unit/target) dibagikan lewat KpiContext supaya
  // konsisten dengan yang dilihat karyawan di My Progress.
  // ========================================================
  const [showKpiForm, setShowKpiForm] = useState(false);
  const [newKpiTitle, setNewKpiTitle] = useState('');
  const [newKpiUnit, setNewKpiUnit] = useState('');
  const [newKpiTarget, setNewKpiTarget] = useState('');

  const handleAddKpi = (e) => {
    e.preventDefault();
    if (!newKpiTitle || !newKpiTarget) return;

    const newId = `kpi-${Date.now()}`;
    setKpis((prev) => [
      ...prev,
      {
        id: newId,
        title: newKpiTitle,
        unit: newKpiUnit || 'unit',
        target: parseFloat(newKpiTarget),
      },
    ]);

    setNewKpiTitle('');
    setNewKpiUnit('');
    setNewKpiTarget('');
    setShowKpiForm(false);
    showToast('KPI berhasil ditambahkan untuk karyawan ini!', 'success');
  };

  const handleDeleteKpi = (kpiId) => {
    const isUsed = weeklyPlans.some((plan) =>
      plan.tasks.some((t) => t.kpiId === kpiId),
    );
    if (isUsed) {
      showToast(
        'KPI tidak bisa dihapus karena masih terhubung dengan aktivitas.',
        'error',
      );
      return;
    }
    setKpis((prev) => prev.filter((k) => k.id !== kpiId));
  };

  // ===========================
  // FETCH EMPLOYEE DETAIL
  // ===========================
  const loadEmployee = async () => {
    const data = await api.getEmployeeDetail({ id });
    setEmployee(data);
  };

  useEffect(() => {
    loadEmployee();
  }, [id]);

  // ===========================
  // LOAD PROGRAM OPTIONS & DIVISIONS
  // ===========================
  useEffect(() => {
    const fetchDataOptions = async () => {
      try {
        const firstTiers = await api.getAllFirstThierProgram();
        setDivisionOptions(firstTiers);

        const ids = firstTiers.map((t) => t.id);
        if (ids.length > 0) {
          const res = await api.getAllSubDivisionOptions({ programId: ids });
          setProgramOptions(res);
        }
      } catch (err) {
        console.error('Error fetching programs/divisions:', err);
      }
    };
    fetchDataOptions();
  }, []);

  // ===========================
  // ADD PROGRAM TO EMPLOYEE
  // ===========================
  const handleAddProgram = async () => {
    if (!selectedProgram) {
      showToast('Silakan pilih program terlebih dahulu.', 'danger');
      return;
    }

    try {
      setAssignLoading(true);

      await api.attendProgramToEmployee({
        employeeId: id,
        programId: selectedProgram,
      });

      showToast('Program berhasil ditambahkan!', 'success');
      setSelectedProgram('');

      await loadEmployee(); // refresh table
    } catch (err) {
      console.error(err);
      showToast(`Gagal menambahkan program, ${err.message || err}`, 'danger');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveProgram = async (programId) => {
    const confirmed = await showConfirm(
      'Apakah Anda yakin ingin menghapus program ini dari karyawan?',
      'Hapus Program',
      'danger',
    );
    if (!confirmed) return;
    try {
      await api.removeProgramFromEmployee({ employeeId: id, programId });
      showToast('Program berhasil dihapus dari karyawan!', 'success');
      await loadEmployee();
    } catch (err) {
      console.error(err);
      showToast(`Gagal menghapus program: ${err.message || err}`, 'danger');
    }
  };

  const handleAddDivision = async () => {
    if (!selectedDivision) {
      showToast('Silakan pilih divisi terlebih dahulu.', 'danger');
      return;
    }
    if (employee.managedDivisions && employee.managedDivisions.length >= 1) {
      showToast('Karyawan maksimal hanya dapat memimpin 1 divisi.', 'danger');
      return;
    }
    try {
      setAssignDivisionLoading(true);
      await api.assignDivisionToEmployee({
        employeeId: id,
        divisionId: selectedDivision,
      });
      showToast('Divisi berhasil dipimpin oleh karyawan!', 'success');
      setSelectedDivision('');
      await loadEmployee();
    } catch (err) {
      console.error(err);
      showToast(`Gagal memimpin divisi: ${err.message || err}`, 'danger');
    } finally {
      setAssignDivisionLoading(false);
    }
  };

  const handleRemoveDivision = async (divisionId) => {
    const confirmed = await showConfirm(
      'Apakah Anda yakin ingin menghapus divisi ini dari kepemimpinan karyawan?',
      'Hapus Kepemimpinan Divisi',
      'danger',
    );
    if (!confirmed) return;
    try {
      await api.removeDivisionFromEmployee({ employeeId: id, divisionId });
      showToast('Kepemimpinan divisi berhasil dihapus!', 'success');
      await loadEmployee();
    } catch (err) {
      console.error(err);
      showToast(`Gagal menghapus divisi: ${err.message || err}`, 'danger');
    }
  };

  const handleDeleteEmployee = async () => {
    const confirmed = await showConfirm(
      'Apakah Anda yakin ingin menghapus karyawan ini secara permanen?',
      'Hapus Karyawan',
      'danger',
    );
    if (!confirmed) return;
    try {
      await api.deleteEmployee({ id });
      showToast('Karyawan berhasil dihapus!', 'success');
      navigate('/employee');
    } catch (err) {
      console.error(err);
      showToast(`Gagal menghapus karyawan: ${err.message || err}`, 'danger');
    }
  };

  // ===========================
  // LOADING STATE
  // ===========================
  if (!employee) {
    return (
      <div className='text-center py-5 text-muted'>
        <div className='spinner-border text-primary mb-3' />
        <p>Memuat detail karyawan...</p>
      </div>
    );
  }

  // ===========================
  // UI RENDER
  // ===========================
  return (
    <div className='container-fluid p-0'>
      {/* Back Button */}
      <div className='mb-3'>
        <button className='btn btn-white btn-sm rounded-pill px-3 shadow-sm border bg-white text-dark fw-semibold' onClick={() => navigate(-1)}>
          <i className='bi bi-arrow-left me-2'></i> Kembali
        </button>
      </div>

      {/* HEADER CARD (Tetap seperti sebelumnya) */}
      <div className='card border-0 shadow-sm rounded-4 mb-4'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-start flex-wrap'>
            <div>
              <h5 className='fw-bold mb-1'>
                <i className='bi bi-person-circle text-primary me-2'></i>
                {employee.firstName} {employee.lastName || ''}
              </h5>
              <p className='text-muted small mb-2'>
                <i className='bi bi-envelope me-1'></i>
                {employee.email || <em>Tidak ada email</em>}
              </p>
              <div>
                <span
                  className={`badge rounded-pill ${employee.systemRole === 'MANAGER' ? 'bg-primary' : 'bg-secondary'}`}
                >
                  <i
                    className={`bi ${employee.systemRole === 'MANAGER' ? 'bi-briefcase-fill' : 'bi-person-fill'} me-1`}
                  ></i>
                  {employee.systemRole === 'MANAGER' ? 'Manager' : 'Staff'}
                </span>
              </div>
            </div>
            <div className='d-flex gap-2 flex-wrap mt-2 mt-md-0'>
              <Link
                to={`/employee/${id}/access`}
                className='btn btn-outline-primary btn-sm rounded-pill px-3'
              >
                <i className='bi bi-shield-lock-fill me-1'></i> Atur Akses
              </Link>
              <Link
                to={`/employee/edit/${id}`}
                className='btn btn-outline-primary btn-sm rounded-pill px-3'
              >
                <i className='bi bi-pencil-square me-1'></i> Edit Profil
              </Link>
              <button
                type='button'
                className='btn btn-outline-danger btn-sm rounded-pill px-3'
                onClick={handleDeleteEmployee}
              >
                <i className='bi bi-trash me-1'></i> Hapus Karyawan
              </button>
            </div>
          </div>

          <div className='mt-3'>
            <div className='row g-2 small text-muted'>
              <div className='col-md-6'>
                <i className='bi bi-card-heading me-1 text-primary'></i>
                <strong>NIP:</strong> {employee.nip || <em>-</em>}
              </div>
              <div className='col-md-6'>
                <i className='bi bi-person-vcard me-1 text-primary'></i>
                <strong>NIK:</strong> {employee.noNik || <em>-</em>}
              </div>
              <div className='col-md-6'>
                <i className='bi bi-calendar2-check me-1 text-primary'></i>
                <strong>Tanggal Mulai:</strong>{' '}
                {employee.dateStartWork || <em>-</em>}
              </div>
              <div className='col-md-6'>
                <i className='bi bi-calendar-x me-1 text-primary'></i>
                <strong>Tanggal Berakhir:</strong>{' '}
                {employee.dateEndWork || <em>-</em>}
              </div>
              <div className='col-md-12'>
                <i className='bi bi-linkedin me-1 text-primary'></i>
                <strong>LinkedIn:</strong>{' '}
                {employee.linkedIn ? (
                  <a
                    href={
                      employee.linkedIn.startsWith('http')
                        ? employee.linkedIn
                        : `https://${employee.linkedIn}`
                    }
                    target='_blank'
                    rel='noreferrer'
                    className='text-decoration-none'
                  >
                    {employee.linkedIn}
                  </a>
                ) : (
                  <em>Tidak ada</em>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TABS NAVIGATION */}
      <ul className='nav nav-pills mb-4 gap-2 bg-white p-2 rounded-4 shadow-sm' style={{ border: '1px solid #dee2e6' }}>
        {isDevOnlyFeatureEnabled() && (
          <li className='nav-item flex-grow-1 flex-md-grow-0'>
            <button
              className={`nav-link w-100 rounded-pill px-4 fw-semibold ${activeTab === 'insights' ? 'active shadow-sm bg-primary text-white' : 'text-secondary'}`}
              onClick={() => setActiveTab('insights')}
            >
              <i className='bi bi-graph-up-arrow me-2'></i> Performance & Attendance
            </button>
          </li>
        )}
        <li className='nav-item flex-grow-1 flex-md-grow-0'>
          <button
            className={`nav-link w-100 rounded-pill px-4 fw-semibold ${activeTab === 'roles' ? 'active shadow-sm bg-primary text-white' : 'text-secondary'}`}
            onClick={() => setActiveTab('roles')}
          >
            <i className='bi bi-person-workspace me-2'></i> Role & Assignments
          </button>
        </li>
      </ul>

      {activeTab === 'roles' && (
        <div>
          {/* =======================
              DIVISI YANG DIPIMPIN (MANAGER - FIRST TIER)
              Hanya relevan untuk employee dengan systemRole MANAGER --
              STAFF tidak pernah memimpin divisi first-tier.
          ======================= */}
      {employee.systemRole === 'MANAGER' && (
      <div className='card border-0 shadow-sm rounded-4 mb-4'>
        <div className='card-body p-4'>
          <div className='d-flex align-items-center mb-3'>
            <i className='bi bi-person-workspace text-primary fs-5 me-2'></i>
            <h6 className='fw-bold mb-0 text-dark'>
              Divisi yang Dipimpin (Manager - First Tier)
            </h6>
          </div>

          {employee.managedDivisions && employee.managedDivisions.length > 0 ? (
            <div className='d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light-subtle'>
              <div className='d-flex align-items-center gap-3'>
                <div
                  className='bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center'
                  style={{ width: '42px', height: '42px' }}
                >
                  <i className='bi bi-building fs-5'></i>
                </div>
                <div>
                  <h6 className='fw-bold text-dark mb-1'>
                    {employee.managedDivisions[0].name}
                  </h6>
                  <span
                    className='badge bg-primary text-white rounded-pill px-2'
                    style={{ fontSize: '0.75rem', fontWeight: 500 }}
                  >
                    Manager / Kepala Divisi
                  </span>
                </div>
              </div>
              <button
                type='button'
                className='btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center'
                style={{ width: '36px', height: '36px' }}
                title='Hapus Divisi'
                onClick={() =>
                  handleRemoveDivision(employee.managedDivisions[0].id)
                }
              >
                <i className='bi bi-trash3-fill fs-6'></i>
              </button>
            </div>
          ) : (
            <div className='text-center py-4 border border-dashed rounded-3 bg-light-subtle'>
              <i className='bi bi-info-circle text-muted fs-4 d-block mb-1'></i>
              <p className='text-muted small mb-0'>
                Karyawan ini tidak memimpin divisi (first tier) manapun.
              </p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* =======================
          PROGRAM / SUB-DIVISI YANG DIKELOLA (STAFF - SECOND TIER)
      ======================= */}
      <div className='card border-0 shadow-sm rounded-4 mb-4'>
        <div className='card-body p-4'>
          <div className='d-flex align-items-center mb-3'>
            <i className='bi bi-diagram-3-fill text-primary fs-5 me-2'></i>
            <h6 className='fw-bold mb-0 text-dark'>
              Program / Sub-Divisi yang Dikelola (Staff - Second Tier)
            </h6>
          </div>

          {employee.responsible && employee.responsible.length > 0 ? (
            <div className='d-flex flex-column gap-2'>
              {employee.responsible.map((prog) => (
                <div
                  key={prog.programId}
                  className='d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light-subtle'
                >
                  <div className='d-flex align-items-center gap-3'>
                    <div
                      className='bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center'
                      style={{ width: '42px', height: '42px' }}
                    >
                      <i className='bi bi-collection-fill fs-5'></i>
                    </div>
                    <div>
                      <h6 className='fw-bold text-dark mb-0'>
                        {prog.programName}
                      </h6>
                      <small
                        className='text-muted'
                        style={{ fontSize: '0.75rem' }}
                      >
                        Second Tier Program
                      </small>
                    </div>
                  </div>
                  <div className='d-flex gap-2'>
                    <button
                      type='button'
                      className='btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center'
                      style={{ width: '32px', height: '32px' }}
                      title='Hapus Program'
                      onClick={() => handleRemoveProgram(prog.programId)}
                    >
                      <i className='bi bi-trash3-fill'></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-4 border border-dashed rounded-3 bg-light-subtle'>
              <i className='bi bi-inbox text-muted fs-4 d-block mb-1'></i>
              <p className='text-muted small mb-0'>
                Tidak ada sub-divisi/program (second tier) yang dikelola.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =======================
          TAMBAH PROGRAM & DIVISI
      ======================= */}
      <div className='row g-4'>
        {/* TAMBAH DIVISI (FIRST TIER) -- khusus MANAGER, STAFF tidak memimpin divisi */}
        {employee.systemRole === 'MANAGER' && (
        <div className='col-md-6'>
          <div className='card border-0 shadow-sm rounded-4 h-100'>
            <div className='card-body d-flex flex-column justify-content-between'>
              <div>
                <div className='d-flex align-items-center mb-3'>
                  <i className='bi bi-person-workspace text-primary me-2'></i>
                  <h6 className='fw-semibold mb-0'>
                    Hubungkan Divisi yang Dipimpin (First Tier)
                  </h6>
                </div>

                <select
                  className='form-select rounded-3 mb-3'
                  value={selectedDivision}
                  onChange={(e) => setSelectedDivision(e.target.value)}
                  disabled={
                    employee.managedDivisions &&
                    employee.managedDivisions.length >= 1
                  }
                >
                  <option value=''>-- Pilih Divisi --</option>
                  {divisionOptions.map((d) => (
                    <option
                      key={d.id}
                      value={d.id}
                    >
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <button
                  className='btn btn-primary rounded-pill w-100'
                  onClick={handleAddDivision}
                  disabled={
                    assignDivisionLoading ||
                    (employee.managedDivisions &&
                      employee.managedDivisions.length >= 1)
                  }
                >
                  {assignDivisionLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Menghubungkan...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-save me-1'></i> Pimpin Divisi
                    </>
                  )}
                </button>
                <small className='text-muted d-block mt-2 text-center'>
                  {employee.managedDivisions &&
                  employee.managedDivisions.length >= 1
                    ? 'Karyawan sudah memimpin divisi. Hapus divisi yang dipimpin saat ini terlebih dahulu untuk memimpin divisi lain.'
                    : 'Karyawan akan ditunjuk sebagai kepala divisi yang dipilih.'}
                </small>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* TAMBAH PROGRAM (SECOND TIER) */}
        <div className={employee.systemRole === 'MANAGER' ? 'col-md-6' : 'col-md-12'}>
          <div className='card border-0 shadow-sm rounded-4 h-100'>
            <div className='card-body d-flex flex-column justify-content-between'>
              <div>
                <div className='d-flex align-items-center mb-3'>
                  <i className='bi bi-plus-circle-fill text-primary me-2'></i>
                  <h6 className='fw-semibold mb-0'>
                    Tambahkan Program ke Karyawan (Second Tier)
                  </h6>
                </div>

                <select
                  className='form-select rounded-3 mb-3'
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                >
                  <option value=''>-- Pilih Program --</option>
                  {programOptions.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                    >
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <button
                  className='btn btn-primary rounded-pill w-100'
                  onClick={handleAddProgram}
                  disabled={assignLoading}
                >
                  {assignLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Menambah...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-save me-1'></i> Tambah Program
                    </>
                  )}
                </button>
                <small className='text-muted d-block mt-2 text-center'>
                  Program yang ditambahkan akan muncul pada sub-divisi yang
                  dikelola.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      )}

      {isDevOnlyFeatureEnabled() && activeTab === 'insights' && (
        <div>
          {/* ========================================================
              POV MANAGER / AUDIT: PERFORMANCE INSIGHTS & ANALYTICS
          ======================================================== */}
      <div className='card border-0 shadow-sm rounded-4 mb-4 overflow-hidden'>
        <div className='card-header border-0 bg-gradient text-white p-4 d-flex align-items-center justify-content-between' style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }}>
          <div>
            <h5 className='fw-bold mb-1 text-white'>
              <i className='bi bi-graph-up-arrow me-2'></i>
              Performance Insights & Analytics
            </h5>
            <p className='mb-0 text-white-50 small'>Review pencapaian target kerja, kelola KPI, dan indeks produktivitas secara kuantitatif</p>
          </div>
          <span className='badge bg-light text-primary rounded-pill fw-semibold px-3 py-2'>
            <i className='bi bi-eye-fill me-1'></i> Manager View
          </span>
        </div>

        <div className='card-body p-4 bg-light-subtle'>
          {/* CRITICAL MANAGER ALERT */}
          <div className='alert alert-primary border-0 rounded-3 p-3 mb-4 d-flex align-items-start gap-3 shadow-sm'>
            <i className='bi bi-info-circle-fill fs-4 text-primary mt-1'></i>
            <div>
              <h6 className='fw-bold mb-1 text-primary-emphasis'>Manager Review Insight</h6>
              <p className='mb-0 text-secondary small'>
                Karyawan ini berkinerja <strong>Sangat Baik</strong> minggu ini dengan <strong>Achievement Rate 98.5%</strong> (40.6 / 41 pts). Rata-rata jam kerja minggu ini <strong>8j 6m</strong> per hari (sesuai standar 8 jam). Tidak ada keterlambatan atau absensi tercatat.
              </p>
            </div>
          </div>

          {/* WFO ATTENDANCE INSIGHT - MANAGER VIEW */}
          <div className='card border-0 shadow-sm rounded-3 p-4 mb-4 bg-white'>
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <h6 className='fw-bold text-dark mb-0'>
                <i className='bi bi-clock-history text-primary me-2'></i>
                WFO Attendance Log (Minggu Ini)
              </h6>
              <div className='d-flex gap-2'>
                <span className='badge bg-success-subtle text-success rounded-pill px-3 py-1'>Avg: 8j 6m / hari</span>
                <span className='badge bg-primary-subtle text-primary rounded-pill px-3 py-1'>Overtime: 0j</span>
              </div>
            </div>
            <div className='table-responsive'>
              <table className='table table-borderless align-middle small mb-0'>
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th>Hari</th>
                    <th>Tanggal</th>
                    <th className='text-center'>Clock-In</th>
                    <th className='text-center'>Clock-Out</th>
                    <th className='text-center'>Durasi</th>
                    <th className='text-center'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { day: 'Senin',  date: '16 Jun 2026', clockIn: '07:00', clockOut: '15:05', total: '8j 5m', status: 'normal' },
                    { day: 'Selasa', date: '17 Jun 2026', clockIn: '07:30', clockOut: '15:35', total: '8j 5m', status: 'normal' },
                    { day: 'Rabu',   date: '18 Jun 2026', clockIn: '08:00', clockOut: '16:10', total: '8j 10m', status: 'normal' },
                    { day: 'Kamis',  date: '19 Jun 2026', clockIn: '07:15', clockOut: '15:20', total: '8j 5m', status: 'normal' },
                    { day: 'Jumat',  date: '20 Jun 2026', clockIn: '07:00', clockOut: '-', total: '-', status: 'active' },
                  ].map((att, idx) => (
                    <tr key={idx} className='border-bottom-subtle'>
                      <td className='fw-semibold'>{att.day}</td>
                      <td className='text-muted'>{att.date}</td>
                      <td className='text-center font-monospace text-success fw-semibold'>{att.clockIn}</td>
                      <td className='text-center font-monospace text-danger fw-semibold'>{att.clockOut}</td>
                      <td className='text-center fw-semibold'>{att.total}</td>
                      <td className='text-center'>
                        {att.status === 'active' ? (
                          <span className='badge bg-primary-subtle text-primary rounded-pill px-2'>
                            <i className='bi bi-circle-fill me-1' style={{ fontSize: '0.5rem' }}></i> Sedang Kerja
                          </span>
                        ) : (
                          <span className='badge bg-success-subtle text-success rounded-pill px-2'>
                            <i className='bi bi-check-lg me-1'></i> 8j+
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* STATISTICS PANEL */}
          <div className='row g-3 mb-4'>
            {weeklyPlans.slice(0, 1).map(plan => {
              const totalPlanned = plan.tasks.reduce((sum, t) => sum + t.weight, 0);
              const totalAchieved = plan.tasks.reduce((sum, t) => sum + (t.weight * (t.completion / 100)), 0);
              const rate = totalPlanned > 0 ? (totalAchieved / totalPlanned) * 100 : 0;
              const completedTasks = plan.tasks.filter(t => t.status === 'DONE').length;

              return (
                <React.Fragment key={plan.id}>
                  {/* Total Velocity Card */}
                  <div className='col-md-4'>
                    <div className='card border-0 shadow-sm rounded-3 p-3 h-100 bg-white'>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <span className='text-muted small fw-semibold'>VELOCITY MINGGU INI</span>
                        <div className='bg-primary-subtle text-primary rounded-pill px-2.5 py-1 small' style={{ fontSize: '0.75rem' }}>
                          <i className='bi bi-lightning-fill'></i>
                        </div>
                      </div>
                      <h3 className='fw-bold mb-1 text-dark'>{totalAchieved.toFixed(1)} <span className='text-muted fs-6 fw-normal'>/ {totalPlanned} pts</span></h3>
                      <div className='progress rounded-pill' style={{ height: '6px' }}>
                        <div 
                          className='progress-bar bg-primary rounded-pill' 
                          role='progressbar' 
                          style={{ width: `${rate}%` }} 
                        />
                      </div>
                      <small className='text-muted d-block mt-2'>{rate.toFixed(0)}% target bobot terpenuhi</small>
                    </div>
                  </div>

                  {/* Completion Rate Card */}
                  <div className='col-md-4'>
                    <div className='card border-0 shadow-sm rounded-3 p-3 h-100 bg-white'>
                      <div className='d-flex align-items-center justify-content-between mb-2'>
                        <span className='text-muted small fw-semibold'>TASK COMPLETION</span>
                        <div className='bg-success-subtle text-success rounded-pill px-2.5 py-1 small' style={{ fontSize: '0.75rem' }}>
                          <i className='bi bi-check2-all'></i>
                        </div>
                      </div>
                      <h3 className='fw-bold mb-1 text-dark'>{completedTasks} <span className='text-muted fs-6 fw-normal'>/ {plan.tasks.length} task</span></h3>
                      <div className='progress rounded-pill' style={{ height: '6px' }}>
                        <div 
                          className='progress-bar bg-success rounded-pill' 
                          role='progressbar' 
                          style={{ width: `${plan.tasks.length > 0 ? (completedTasks / plan.tasks.length) * 100 : 0}%` }} 
                        />
                      </div>
                      <small className='text-muted d-block mt-2'>{plan.tasks.length - completedTasks} tugas tersisa</small>
                    </div>
                  </div>

                  {/* Velocity Trend Card (SVG Chart) */}
                  <div className='col-md-4'>
                    <div className='card border-0 shadow-sm rounded-3 p-3 h-100 bg-white'>
                      <span className='text-muted small fw-semibold mb-2'>TREN PERFORMASI (4 MINGGU TERAKHIR)</span>
                      <div className='d-flex align-items-end justify-content-between h-75 pt-2'>
                        {[
                          { week: 'W22', plan: 15, get: 12 },
                          { week: 'W23', plan: 18, get: 18 },
                          { week: 'W24', plan: 14, get: 10 },
                          { week: 'W25', plan: totalPlanned, get: totalAchieved }
                        ].map((w, idx) => {
                          const maxPt = 20;
                          const heightPct = (w.get / maxPt) * 100;
                          const planHeightPct = (w.plan / maxPt) * 100;
                          return (
                            <div key={idx} className='text-center d-flex flex-column align-items-center w-25 position-relative'>
                              <div className='d-flex align-items-end gap-1 position-relative mb-1' style={{ height: '50px', width: '32px' }}>
                                {/* Planned line */}
                                <div className='bg-secondary-subtle rounded-top position-absolute bottom-0' style={{ width: '6px', height: `${planHeightPct}%`, left: '4px', opacity: 0.5 }} title={`Planned: ${w.plan}`} />
                                {/* Achieved Line */}
                                <div className='bg-primary rounded-top position-absolute bottom-0' style={{ width: '6px', height: `${heightPct}%`, left: '16px' }} title={`Achieved: ${w.get}`} />
                              </div>
                              <span className='text-muted' style={{ fontSize: '0.7rem' }}>{w.week}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* KPI MANAGEMENT - milik karyawan, tidak terikat ke satu periode.
              Task di periode manapun bisa terhubung ke KPI yang sama. */}
          <div className='card border-0 shadow-sm rounded-3 mb-4 p-4 bg-white'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <h6 className='fw-bold text-dark mb-1'>
                  <i className='bi bi-bullseye text-primary me-2'></i>
                  Manajemen KPI Karyawan
                </h6>
                <span className='text-muted small'>
                  KPI berlaku untuk semua periode. Actual dihitung otomatis dari
                  progress task manapun yang terhubung ke KPI ini.
                </span>
              </div>
              <button
                type='button'
                className='btn btn-sm btn-outline-primary rounded-pill px-3'
                onClick={() => setShowKpiForm((prev) => !prev)}
              >
                <i className='bi bi-plus-lg me-1'></i> Tambah KPI
              </button>
            </div>

            {kpis.length === 0 && !showKpiForm && (
              <div className='text-muted small mb-3'>
                Belum ada KPI untuk karyawan ini.
              </div>
            )}

            {kpis.length > 0 && (
              <div className='row g-3 mb-3'>
                {kpis.map((kpi) => {
                  const linkedTasks = weeklyPlans.flatMap((plan) =>
                    plan.tasks.filter((t) => t.kpiId === kpi.id),
                  );
                  const actual = linkedTasks.reduce((sum, t) => sum + (t.completion || 0) / 100, 0);
                  const achievement = kpi.target > 0 ? Math.min(200, (actual / kpi.target) * 100) : 0;
                  return (
                    <div className='col-md-6 col-xl-4' key={kpi.id}>
                      <div className='p-3 rounded-3 bg-light-subtle border h-100'>
                        <div className='d-flex justify-content-between align-items-start mb-2'>
                          <div>
                            <strong className='text-dark d-block'>{kpi.title}</strong>
                            <span className='text-muted small'>{linkedTasks.length} aktivitas (semua periode)</span>
                          </div>
                          <button
                            type='button'
                            className='btn btn-sm btn-link text-danger p-0'
                            title='Hapus KPI'
                            onClick={() => handleDeleteKpi(kpi.id)}
                          >
                            <i className='bi bi-trash'></i>
                          </button>
                        </div>
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <strong className='text-dark'>{Number.isInteger(actual) ? actual : actual.toFixed(1)}</strong>
                          <span className='text-muted small'>/ {kpi.target} {kpi.unit}</span>
                        </div>
                        <div className='progress' style={{ height: '6px' }}>
                          <div
                            className={`progress-bar ${achievement >= 100 ? 'bg-success' : achievement >= 50 ? 'bg-primary' : 'bg-warning'}`}
                            role='progressbar'
                            style={{ width: `${Math.min(100, achievement)}%` }}
                          />
                        </div>
                        <span className='small fw-bold text-muted d-block text-end mt-1'>{achievement.toFixed(0)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showKpiForm && (
              <form
                onSubmit={handleAddKpi}
                className='row g-2 align-items-end p-3 rounded-3 bg-light-subtle border'
              >
                <div className='col-md-4'>
                  <label className='form-label small fw-semibold text-muted mb-1'>
                    Judul KPI *
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-sm rounded-3'
                    placeholder='contoh: Leads Baru'
                    value={newKpiTitle}
                    onChange={(e) => setNewKpiTitle(e.target.value)}
                    required
                  />
                </div>
                <div className='col-md-3'>
                  <label className='form-label small fw-semibold text-muted mb-1'>
                    Satuan
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-sm rounded-3'
                    placeholder='leads, %, dok'
                    value={newKpiUnit}
                    onChange={(e) => setNewKpiUnit(e.target.value)}
                  />
                </div>
                <div className='col-md-2'>
                  <label className='form-label small fw-semibold text-muted mb-1'>
                    Target *
                  </label>
                  <input
                    type='number'
                    className='form-control form-control-sm rounded-3'
                    value={newKpiTarget}
                    onChange={(e) => setNewKpiTarget(e.target.value)}
                    min='0'
                    required
                  />
                </div>
                <div className='col-md-3 d-flex gap-2'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary btn-sm rounded-pill w-100'
                    onClick={() => setShowKpiForm(false)}
                  >
                    Batal
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary btn-sm rounded-pill w-100 fw-semibold'
                  >
                    Simpan
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* ACTIVE WEEKLY PROGRESS WORKSPACE */}
          {weeklyPlans.map((plan) => (
            <div key={plan.id} className='card border-0 shadow-sm rounded-3 mb-4 p-4 bg-white'>
              <div className='d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom flex-wrap gap-2'>
                <div>
                  <h6 className='fw-bold mb-1 text-dark'>
                    <i className='bi bi-calendar-range me-2 text-primary'></i>
                    Periode: {plan.period}
                  </h6>
                  <span className={`badge ${plan.status === 'EVALUATED' ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} small`}>
                    {plan.status === 'EVALUATED' ? 'Sudah Dievaluasi' : 'Menunggu Evaluasi'}
                  </span>
                </div>
                <div className='text-end'>
                  <span className='text-muted small d-block'>Total Weight</span>
                  <strong className='fs-5 text-primary'>{plan.tasks.reduce((sum, t) => sum + (t.weight * (t.completion / 100)), 0).toFixed(1)} / {plan.tasks.reduce((sum, t) => sum + t.weight, 0)} pts</strong>
                </div>
              </div>

              {/* TASK ITEMS LIST */}
              <div className='table-responsive'>
                <table className='table table-hover align-middle table-borderless'>
                  <thead>
                    <tr className='text-muted small border-bottom'>
                      <th style={{ width: '30%' }}>Aktivitas / Deskripsi Laporan</th>
                      <th style={{ width: '15%' }}>Hari / Tanggal</th>
                      <th style={{ width: '15%' }}>KPI Terkait</th>
                      <th className='text-center' style={{ width: '10%' }}>Bobot</th>
                      <th style={{ width: '15%' }}>Bukti (Here)</th>
                      <th style={{ width: '15%' }}>Progress & Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.tasks.length === 0 ? (
                      <tr>
                        <td colSpan='6' className='text-center py-4 text-muted'>
                          <i className='bi bi-journal-x fs-3 d-block mb-1'></i>
                          Belum ada aktivitas tercatat untuk periode ini.
                        </td>
                      </tr>
                    ) : (
                      plan.tasks.map((task) => (
                        <tr key={task.id} className='border-bottom-subtle'>
                          <td>
                            <div className='fw-semibold text-dark mb-1' style={{ fontSize: '0.925rem' }}>{task.title}</div>
                            <span className={`badge px-2 py-1 ${
                              task.status === 'DONE' ? 'bg-success-subtle text-success' :
                              task.status === 'IN_PROGRESS' ? 'bg-warning-subtle text-warning' : 'bg-secondary-subtle text-secondary'
                            }`} style={{ fontSize: '0.7rem' }}>
                              {task.status}
                            </span>
                          </td>
                          <td>
                            <span className='text-secondary small d-block'>{task.date}</span>
                          </td>
                          <td>
                            {task.kpiId ? (
                              <span className='badge bg-primary-subtle text-primary px-2 py-1' style={{ fontSize: '0.7rem' }}>
                                <i className='bi bi-bullseye me-1'></i>
                                {kpis.find((k) => k.id === task.kpiId)?.title || '-'}
                              </span>
                            ) : (
                              <span className='text-muted small'>-</span>
                            )}
                          </td>
                          <td className='text-center'>
                            <span className='badge bg-light text-dark border px-2.5 py-1.5 fw-semibold' style={{ fontSize: '0.85rem' }}>
                              {task.weight}
                            </span>
                          </td>
                          <td>
                            {task.evidenceUrl ? (
                              <a href={task.evidenceUrl} target='_blank' rel='noreferrer' className='btn btn-link btn-sm text-primary fw-semibold p-0'>
                                <i className='bi bi-link-45deg me-1'></i> Here
                              </a>
                            ) : (
                              <span className='text-muted small'>TBA</span>
                            )}
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <div className='progress flex-grow-1' style={{ height: '6px' }}>
                                <div
                                  className={`progress-bar ${task.completion === 100 ? 'bg-success' : 'bg-primary'}`}
                                  role='progressbar'
                                  style={{ width: `${task.completion}%` }}
                                />
                              </div>
                              <span className='small fw-bold text-muted'>{task.completion}%</span>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
